'use client';

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { normalizeMediaFileUrl } from '@/utils/media-file-url';
import { CastMember, Movie } from '@cinemark/content-api-client';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

const SCROLL_BY_SIZE = 5;

type MovieDetailCastProps = {
  dataLinkCategory: string;
  movie: Movie;
};

export const MovieDetailCast = ({ dataLinkCategory, movie }: Readonly<MovieDetailCastProps>): React.JSX.Element | null => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const castMembers = movie.castMembers || [];
  const [visibleItems, setVisibleItems] = useState(1);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const { trackClick } = useDataAnalytics();

  useEffect(() => {
    if (!containerRef.current || !itemRef.current) return;

    const updateVisibleItems = (): void => {
      const containerWidth = containerRef.current!.offsetWidth;
      const itemWidth = itemRef.current!.offsetWidth;
      if (itemWidth) {
        const maxVisible = Math.max(1, Math.floor(containerWidth / itemWidth));
        const visible = Math.min(maxVisible, castMembers.length);
        setVisibleItems(visible);
      }
    };

    updateVisibleItems();

    const resizeObserver = new ResizeObserver(updateVisibleItems);
    resizeObserver.observe(containerRef.current);

    return (): void => resizeObserver.disconnect();
  }, [castMembers.length]);

  const displayItem = (castMember: CastMember): React.JSX.Element => {
    const displayName = castMember.fullName || castMember.characterName || 'Unknown';
    const image = castMember.largeImage || castMember.smallImage;
    const imageUrl = normalizeMediaFileUrl(image?.url) || '/img/placeholder_cast.png';

    return (
      <div className="flex flex-col gap-sm">
        <Image alt={`Cast Member - ${displayName}`}
          className="w-full h-full object-cover aspect-square rounded-md"
          height={image?.height || 100}
          src={imageUrl}
          width={image?.width || 100}
        />

        <p className="line-clamp-2 break-words font-semibold text-sm"
          data-test={`cast-member-${dataTestTransformer(displayName)}`}
        >
          {displayName}
        </p>
      </div>
    );
  };

  if (!castMembers?.length) return null;

  return (
    <div className="flex flex-col gap-sm">
      <h4 data-test="media-title">Cast</h4>

      <div ref={containerRef}
        className="w-full"
      >
        {/* Invisible item for measurement */}
        <div className="absolute invisible pointer-events-none h-0 overflow-hidden">
          <div ref={itemRef}>
            {displayItem(castMembers[0])}
          </div>
        </div>

        <Carousel opts={{
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: true,
          skipSnaps: true,
          slidesToScroll: 1,
          watchDrag: true,
          watchResize: true,
        }}
        visibleItems={visibleItems}
        setApi={(api) => {
          if (api) {
            setCarouselApi(api);
            const updateButtons = (): void => {
              const progress = api.scrollProgress();
              setCanScrollPrev(progress > 0.01);
              setCanScrollNext(progress < 0.99);
            };
            updateButtons();
            api.on('scroll', updateButtons);
            api.on('select', updateButtons);
            api.on('settle', updateButtons);
          }
        }}
        >
          <CarouselContent className="items-stretch">
            {castMembers.map(castMember =>
              <CarouselItem key={castMember.id}
                className="min-w-[100px] max-w-[100px]"
              >
                {displayItem(castMember)}
              </CarouselItem>
            )}
          </CarouselContent>
          {canScrollPrev &&
            <CarouselPrevious className="size-12 -left-7 top-10 hidden md:flex"
              dataTest="button-movie-cast-carousel-previous"
              iconFontSizeClassName="size-8"
              variant="secondary"
              onClick={(e) => {
                e.preventDefault();
                trackClick({
                  linkCategory: dataLinkCategory,
                  linkName: 'MDP|Cast|Previous',
                });
                if (carouselApi) {
                  carouselApi.scrollTo(Math.max(0, carouselApi.selectedScrollSnap() - SCROLL_BY_SIZE));
                }
              }}
            />
          }
          {canScrollNext && castMembers.length > 6 &&
            <CarouselNext className="size-12 -right-7 top-10 hidden md:flex"
              dataTest="button-movie-cast-carousel-next"
              iconFontSizeClassName="size-8"
              variant="secondary"
              onClick={(e) => {
                e.preventDefault();
                trackClick({
                  linkCategory: dataLinkCategory,
                  linkName: 'MDP|Cast|Next',
                });
                if (carouselApi) {
                  carouselApi.scrollTo(Math.min(carouselApi.scrollSnapList().length - 1, carouselApi.selectedScrollSnap() + SCROLL_BY_SIZE));
                }
              }}
            />
          }
        </Carousel>
      </div>
    </div>
  );
};
